import { useCallback, useMemo, Fragment } from 'react';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Text } from 'foundations-components/components/Text';
import { HR } from 'foundations-components/transitional/components/HR';
import { View } from 'foundations-components/transitional/components/View';
import { Select } from 'foundations-components/transitional/components/Select';
import { Flex } from 'foundations-components/components/Flex';
import { SearchInput } from 'foundations-components/transitional/components/SearchInput';
import { useResponsiveProps } from 'marketplace-ui-common/hooks/useResponsiveProps';
import { getIndustryOptions, getEmployeesOptions } from '../utils/options';
import { FILTER_KEY, FILTER, INDUSTRY_KEY, EMPLOYEES_KEY, SERVICE_KEY, SORT_KEY, SORT } from '../constants/ReviewsListQueries';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ValueComponent = ({
  children
}) => /*#__PURE__*/_jsx("span", {
  role: "button",
  children: children
});
export default function ReviewsFilterSortDropdowns({
  catalogServices,
  excludedFilterOptions,
  hiddenDropdowns,
  onUpdateFilters,
  reviewFilters,
  reviewStats,
  showDivider = true,
  showSearchBar = false,
  inline = true
}) {
  const {
    display
  } = useResponsiveProps({
    display: {
      xs: 'block',
      md: 'inline-block'
    }
  });
  const {
    buttonUse
  } = useResponsiveProps({
    buttonUse: {
      xs: 'link',
      lg: 'transparent'
    }
  });
  const validFilters = useMemo(() => reviewStats.getValidRatingFilters(), [reviewStats]);
  const buildDropdown = useCallback((textKey, test) => {
    const options = Object.keys(test).reduce((result, option) => {
      if (!excludedFilterOptions || !excludedFilterOptions.includes(option)) {
        result.push({
          text: I18n.text(`marketplace-ui-reviews.list.${textKey}.${option}`),
          value: option,
          disabled: textKey === FILTER_KEY && option !== FILTER.ALL && !validFilters.has(option),
          'data-test-id': `${textKey}-option-${option.toLowerCase()}`
        });
      }
      return result;
    }, []);
    return /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      marginInlineEnd: "400",
      children: [/*#__PURE__*/_jsx(View, {
        display: display,
        marginInlineEnd: "300",
        children: /*#__PURE__*/_jsx(Text, {
          marginBlockEnd: "-50",
          typography: "body-125",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `marketplace-ui-reviews.list.${textKey}.label`
          })
        })
      }), /*#__PURE__*/_jsx(Select, {
        buttonUse: buttonUse,
        options: options,
        menuWidth: 250,
        marginInlineStart: "0 !important",
        padding: "0 !important",
        display: "inline-block",
        value: reviewFilters.get(textKey),
        onChange: ({
          target: {
            value
          }
        }) => onUpdateFilters({
          textKey,
          value,
          source: 'dropdown filter'
        }),
        "data-test-id": `${textKey}-reviews-list-select`,
        valueComponent: ValueComponent
      }, textKey)]
    });
  }, [buttonUse, display, excludedFilterOptions, onUpdateFilters, reviewFilters, validFilters]);
  const renderedFilterDropdown = buildDropdown(FILTER_KEY, FILTER);
  const renderedSortDropdown = buildDropdown(SORT_KEY, SORT);
  const industryOptions = useMemo(getIndustryOptions, []);
  const renderedIndustryDropdown = /*#__PURE__*/_jsx(View, {
    as: "span",
    marginInlineEnd: "400",
    children: /*#__PURE__*/_jsx(Select, {
      buttonUse: buttonUse,
      placeholder: I18n.text('marketplace-ui-reviews.industry.placeholder'),
      options: industryOptions,
      marginInlineStart: "0 !important",
      padding: "0 !important",
      menuWidth: 250,
      display: "inline-block",
      value: reviewFilters.get(INDUSTRY_KEY),
      onChange: ({
        target: {
          value = ''
        }
      }) => onUpdateFilters({
        textKey: INDUSTRY_KEY,
        value,
        source: 'dropdown filter'
      }),
      clearable: true,
      "data-test-id": "industry-reviews-list-select",
      valueComponent: ValueComponent
    }, INDUSTRY_KEY)
  });
  const employeesOptions = useMemo(getEmployeesOptions, []);
  const renderedEmployeesDropdown = /*#__PURE__*/_jsx(View, {
    as: "span",
    marginInlineEnd: "400",
    children: /*#__PURE__*/_jsx(Select, {
      buttonUse: buttonUse,
      placeholder: I18n.text('marketplace-ui-reviews.employees.placeholder'),
      options: employeesOptions,
      marginInlineStart: "0 !important",
      padding: "0 !important",
      menuWidth: 250,
      display: "inline-block",
      value: reviewFilters.get(EMPLOYEES_KEY),
      onChange: ({
        target: {
          value = ''
        }
      }) => onUpdateFilters({
        textKey: EMPLOYEES_KEY,
        value,
        source: 'dropdown filter'
      }),
      clearable: true,
      "data-test-id": "employees-reviews-list-select",
      valueComponent: ValueComponent
    }, EMPLOYEES_KEY)
  });
  const renderedCatalogServicesDropdown = catalogServices && /*#__PURE__*/_jsx(View, {
    as: "span",
    marginInlineEnd: "400",
    children: /*#__PURE__*/_jsx(Select, {
      buttonUse: buttonUse,
      placeholder: I18n.text('marketplace-ui-reviews.services.placeholder'),
      options: catalogServices.map(({
        id,
        name
      }) => ({
        value: id,
        text: name
      })),
      marginInlineStart: "0 !important",
      padding: "0 !important",
      menuWidth: 250,
      display: "inline-block",
      value: reviewFilters.get(SERVICE_KEY.toLowerCase()),
      onChange: ({
        target: {
          value = ''
        }
      }) => onUpdateFilters({
        textKey: SERVICE_KEY,
        value,
        source: 'dropdown filter'
      }),
      clearable: true,
      "data-test-id": "services-reviews-list-select",
      valueComponent: ValueComponent
    }, SERVICE_KEY)
  });
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      justifyContent: "space-between",
      flexDirection: inline ? 'row' : 'column-reverse',
      gap: "200",
      paddingBlock: "200",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexWrap: "wrap",
        flexDirection: "row",
        alignSelf: inline ? 'center' : 'start',
        children: [/*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          flexWrap: "wrap",
          marginInlineEnd: "300",
          children: [!(hiddenDropdowns !== null && hiddenDropdowns !== void 0 && hiddenDropdowns.includes('filter')) && renderedFilterDropdown, !(hiddenDropdowns !== null && hiddenDropdowns !== void 0 && hiddenDropdowns.includes('industry')) && renderedIndustryDropdown, !(hiddenDropdowns !== null && hiddenDropdowns !== void 0 && hiddenDropdowns.includes('employees')) && renderedEmployeesDropdown, !(hiddenDropdowns !== null && hiddenDropdowns !== void 0 && hiddenDropdowns.includes('service')) && renderedCatalogServicesDropdown]
        }), !(hiddenDropdowns !== null && hiddenDropdowns !== void 0 && hiddenDropdowns.includes('sort')) && renderedSortDropdown]
      }), showSearchBar && /*#__PURE__*/_jsx(View, {
        inlineSize: inline ? '30%' : '100%',
        minInlineSize: "200px",
        children: /*#__PURE__*/_jsx(SearchInput, {
          value: reviewFilters.search,
          onChange: ({
            target: {
              value
            }
          }) => {
            onUpdateFilters({
              textKey: 'search',
              value
            });
          }
        })
      })]
    }), showDivider && /*#__PURE__*/_jsx(HR, {
      distance: "extra-small"
    })]
  });
}